import { ProjectOutline } from '../../types/outline';
import type { EmpiricalCodebook } from '../../types/empirical';

// Re-export for convenience within slices
export type { ProjectOutline };
export type { EmpiricalCodebook } from '../../types/empirical';

// Interface para respostas do onboarding (Eixo Subjetivo)
export interface ProjectOnboardingAnswers {
  knowledgeLevel: 'none' | 'basic' | 'intermediate' | 'advanced';
  researchGoal: 'explore' | 'review' | 'deepen' | 'master';
  fieldExperience: 'none' | 'superficial' | 'moderate' | 'deep';
  availableTime: 'urgent' | 'limited' | 'moderate' | 'ample';
  researchStage: 'starting' | 'exploring' | 'writing' | 'finishing';
}

// Interface para Grupos de Dados de Pesquisa (Fase 3)
export interface DataGroup {
  id: string;
  name: string;
  color?: string;
  order: number;
  createdAt: string;
}

// ============================================
// CAMPOS CUSTOMIZÁVEIS (Fase 7)
// ============================================

// Tipos de campo suportados
export type FieldType = 'text' | 'number' | 'date' | 'select' | 'boolean';

// Definição de um campo customizável do projeto
export interface ProjectField {
  id: string;                      // UUID único
  name: string;                    // Nome do campo (ex: "Participante")
  type: FieldType;                 // Tipo do campo
  options?: string[];              // Opções para campos tipo 'select'
  order: number;                   // Ordem de exibição (0, 1, 2...)
  required?: boolean;              // Campo obrigatório? (default: false)
  showInTable?: boolean;           // Mostrar como coluna na tabela? (default: false)
  includeInCitation?: boolean;     // Incluir na citação ABNT? (default: false)
  createdAt: string;               // Data de criação (ISO string)
  updatedAt?: string;              // Data de última atualização (ISO string)
}

// Valor preenchido de um campo em um dado específico
export interface FieldValue {
  fieldId: string;                 // Referência ao ProjectField.id
  value: string | number | boolean | null;
}

// ============================================
// LENTES DE PESQUISA (Fase 6)
// ============================================

// Papel/função da referência na análise do dado empírico
export type LensRole = 'central' | 'support' | 'contrast' | 'context';

// Interface para uma Lente de Pesquisa (conexão dado↔referência)
export interface DataLens {
  id: string;
  referenceId: string;     // workId da referência
  role: LensRole;          // papel: central, apoio, contraponto, contexto
  note?: string;           // nota interpretativa do pesquisador
  createdAt: string;
  updatedAt?: string;
}

// ============================================
// MARCADORES TEMPORAIS (Fase 5)
// ============================================

// Interface para um Marcador Temporal (momento específico em áudio/vídeo)
export interface TimeMarker {
  id: string;
  time: number;            // Tempo em segundos
  label: string;           // Título curto do momento (ex: "Fala sobre pertencimento")
  note?: string;           // Nota/observação do pesquisador
  referenceId?: string;    // workId da referência teórica (opcional, conecta com Lentes)
  createdAt: string;
  updatedAt?: string;
}

// ============================================
// DADOS DE PESQUISA DO PROJETO (v4.0)
// ============================================

// Interface para um dado de pesquisa vinculado ao projeto
export interface ProjectResearchData {
  id: string;
  projectId?: string;
  repositoryItemId: string;
  addedAt: string;
  tags?: string[];
  relevance?: 'central' | 'important' | 'doubt' | null;
  status?: 'pending' | 'analyzing' | 'analyzed' | null;
  isFavorite?: boolean;
  notes?: string;
  lenses?: DataLens[];
  markers?: TimeMarker[];
  imageAnnotations?: string;  // v4.0: Anotações de imagem (JSON)
  imageRotation?: number;     // v4.0: Rotação da imagem (0, 90, 180, 270)
  groupId?: string | null;
  fieldValues?: FieldValue[];
  integrationTags?: string[];
  notesCount?: number;
  participantId?: string;
  synthesis?: string;
  order?: number;
  isUsed?: boolean;
}

// ============================================

// Configuração de papéis das lentes
export const LENS_ROLES: Record<LensRole, { label: string; color: string; icon: string; description: string }> = {
  central: {
    label: 'Lente Central',
    color: '#D4918A',
    icon: '⭐',
    description: 'Base teórica principal para interpretar este dado'
  },
  support: {
    label: 'Apoio',
    color: '#477B72',
    icon: '📎',
    description: 'Referência que complementa a análise'
  },
  contrast: {
    label: 'Contraponto',
    color: '#F2BC41',
    icon: '⚡',
    description: 'Perspectiva divergente ou tensão teórica'
  },
  context: {
    label: 'Contexto',
    color: '#8B5CF6',
    icon: '🔍',
    description: 'Referência para contextualização'
  }
};

// ============================================
// CRUZAMENTOS DO PROJETO
// ============================================

export interface ProjectCrossing {
  tagName: string;
  customTitle?: string;
  comments?: string;
}

// ============================================
// REFERÊNCIA DO PROJETO (v5.0)
// ============================================

export interface ProjectReference {
  workId: string;
  archetype?: string | null;
  readStatus: string;
  order: number;
  addedAt: string;
  relevance?: string | null;
  integrationTags: string[];
  isFavorite: boolean;
  status?: 'pending' | 'analyzing' | 'analyzed' | null;
  isCited?: boolean;
  isFlagged?: boolean;
  classifiedWithAI?: boolean;
  cardColor?: string;
  stickyNote?: string;
  citationPage?: string;
  highlightCount?: number;
  lastPage?: number;
}

// ============================================

export interface DriveProject {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  status: 'active' | 'archived' | 'trashed';
  isActive: boolean;
  archivedAt?: string;
  trashedAt?: string;
  daysRemaining?: number;
  // Onboarding do pesquisador (Eixo Subjetivo) - preenchido 1x por projeto
  onboarding?: ProjectOnboardingAnswers;
  onboardingCompletedAt?: string;
  // NOVO: Outline do projeto (Fase 6)
  outline?: ProjectOutline;
  // NOVO: Campos customizáveis do projeto (Fase 7)
  customFields?: ProjectField[];
  /**
   * @deprecated Campo fantasma — nunca persistiu (silent drop na escrita,
   * ausente do load; auditoria 015, Q-S15-2). Mantido apenas porque 3
   * leitores do flanco empírico compilam contra ele (AnalysisTechniquesModal,
   * HighlightCard, useEmpiricalTags — Q-S15-7). Destino:
   * remoção definitiva na sprint do flanco empírico que corrigir o mismatch slug×label (auditoria 015, Q-S15-7).
   */
  area?: string;
  // Bigwa na Medida
  template?: 'pesquisa' | 'leitura' | 'personalizado';
  visiblePages?: {
    dadosPesquisa: boolean;
    conexoes: boolean;
    citacoes: boolean;
    colaboracao: boolean;
    arquivo: boolean;
  };
  pageLabels?: Record<string, string>;
  sidebarDividers?: boolean | null;
  // Fase Empírica v4 — DN8: livro de códigos empírico (Mesa-level)
  // - Colunas e categorias declaradas pelo pesquisador (DN5)
  // - IDs estáveis para rename atômico via single-write em project.json (DN13)
  // - Auto-populated por migration v5→v6 a partir de labels antigos (DN9)
  // - undefined = Mesa sem livro de códigos ainda (Wizard DN23 aparece)
  empiricalCodebook?: EmpiricalCodebook;
}

export interface DriveProjectStore {
  projects: DriveProject[];
  isLoading: boolean;
  error: string | null;
  activeProjectId: string | null;
  projectsLoaded: boolean;
  projectsLoadedAt: number | null;

  // Cache de referências do projeto ativo
  cachedReferences: ProjectReference[];
  referencesLoaded: boolean;
  referencesLoadedAt: number | null;
  isLoadingReferences: boolean;

  // Cache de dados de pesquisa do projeto ativo
  cachedResearchData: ProjectResearchData[];
  researchDataLoaded: boolean;
  researchDataLoadedAt: number | null;
  isLoadingResearchData: boolean;

  // Cache de dados de cruzamentos do projeto ativo
  cachedCrossings: ProjectCrossing[];
  crossingsLoaded: boolean;
  crossingsLoadedAt: number | null;

  // NOVO: Cache de outline do projeto ativo
  cachedOutline: ProjectOutline | null;
  outlineLoaded: boolean;
  outlineLoadedAt: number | null;

  // NOVO: Cache de grupos de dados do projeto ativo (Fase 3)
  cachedGroups: DataGroup[];
  groupsLoaded: boolean;
  groupsLoadedAt: number | null;

  // NOVO: Cache de campos customizáveis do projeto ativo (Fase 7)
  cachedCustomFields: ProjectField[];
  customFieldsLoaded: boolean;
  customFieldsLoadedAt: number | null;

  // Funções de projetos
  loadProjects: () => Promise<void>;
  invalidateProjectsCache: () => void;
  createProject: (name: string, description: string, template?: 'pesquisa' | 'leitura' | 'personalizado', visiblePages?: Record<string, boolean>, pageLabels?: Record<string, string>, sidebarDividers?: boolean | null) => Promise<void>;
  updateProject: (id: string, updates: Partial<DriveProject>) => Promise<void>;
  // Fase Empírica v4 (Sprint 0.2) — DN8/DN13: update isolado do codebook via updateProjectJson
  // (updateProject genérico tem allowlist e faz silent drop de empiricalCodebook)
  updateProjectCodebook: (id: string, codebook: EmpiricalCodebook) => Promise<void>;
  archiveProject: (id: string) => Promise<string | null>;
  deleteProject: (id: string) => Promise<string | null>;
  restoreProject: (id: string) => Promise<void>;
  deletePermanently: (id: string) => Promise<void>;
  setActiveProject: (id: string) => void;
  getActiveProject: () => DriveProject | undefined;
  getActiveProjects: () => DriveProject[];
  getArchivedProjects: () => DriveProject[];
  getTrashedProjects: () => DriveProject[];

  // Funções de onboarding do projeto
  hasProjectOnboarding: (projectId?: string) => boolean;
  saveProjectOnboarding: (answers: ProjectOnboardingAnswers) => Promise<void>;
  getProjectOnboarding: (projectId?: string) => ProjectOnboardingAnswers | null;
  clearProjectOnboarding: (projectId: string) => Promise<void>;

  // Funções de referências do projeto (com cache)
  loadActiveProjectReferences: () => Promise<ProjectReference[]>;
  addReferenceToActiveProject: (workId: string) => Promise<void>;
  addReferenceToProject: (workId: string, targetProjectId: string) => Promise<void>;
  removeReferenceFromActiveProject: (workId: string) => Promise<void>;
  getActiveProjectReferences: () => Promise<ProjectReference[]>;
  getCachedReferences: () => ProjectReference[];
  updateReferenceInActiveProject: (workId: string, updates: Partial<ProjectReference>) => Promise<void>;
  invalidateReferencesCache: () => void;
  revalidateReferencesIfStale: () => void;

  // Funções de dados de pesquisa do projeto (com cache)
  revalidateResearchDataIfStale: () => void;
  loadActiveProjectResearchData: () => Promise<ProjectResearchData[]>;
  addResearchDataToActiveProject: (repositoryItemId: string) => Promise<string>;
  removeResearchDataFromActiveProject: (dataId: string) => Promise<void>;
  updateResearchDataInActiveProject: (dataId: string, updates: Partial<ProjectResearchData>) => Promise<void>;
  getCachedResearchData: () => ProjectResearchData[];
  invalidateResearchDataCache: () => void;

  // Funções de cruzamentos do projeto (com cache)
  loadActiveProjectCrossings: () => Promise<ProjectCrossing[]>;
  updateCrossingInActiveProject: (tagName: string, updates: {
    customTitle?: string;
    comments?: string;
  }) => Promise<void>;
  getCachedCrossings: () => ProjectCrossing[];
  invalidateCrossingsCache: () => void;
  revalidateCrossingsIfStale: () => void;

  // NOVO: Funções de outline do projeto (Fase 6)
  loadActiveProjectOutline: () => Promise<ProjectOutline>;
  saveActiveProjectOutline: () => Promise<void>;
  getCachedOutline: () => ProjectOutline | null;
  invalidateOutlineCache: () => void;
  revalidateOutlineIfStale: () => void;

  // NOVO: Funções de grupos de dados (Fase 3)
  loadActiveProjectGroups: () => Promise<DataGroup[]>;
  createGroupInActiveProject: (name: string, color?: string) => Promise<string>;
  updateGroupInActiveProject: (groupId: string, updates: { name?: string; color?: string; order?: number }) => Promise<void>;
  deleteGroupFromActiveProject: (groupId: string) => Promise<void>;
  reorderGroupsInActiveProject: (groupIds: string[]) => Promise<void>;
  assignDataToGroup: (dataId: string, groupId: string | null) => Promise<void>;
  getCachedGroups: () => DataGroup[];
  invalidateGroupsCache: () => void;
  revalidateGroupsIfStale: () => void;

  // ============================================
  // NOVO: Funções de Lentes de Pesquisa (Fase 6)
  // ============================================
  addLensToData: (dataId: string, referenceId: string, role: LensRole, note?: string) => Promise<string>;
  updateLensInData: (dataId: string, lensId: string, updates: { role?: LensRole; note?: string }) => Promise<void>;
  removeLensFromData: (dataId: string, lensId: string) => Promise<void>;
  getDataLenses: (dataId: string) => DataLens[];

  // ============================================
  // NOVO: Funções de Marcadores Temporais (Fase 5)
  // ============================================
  addTimeMarker: (dataId: string, marker: Omit<TimeMarker, 'id' | 'createdAt'>) => Promise<string>;
  updateTimeMarker: (dataId: string, markerId: string, updates: Partial<TimeMarker>) => Promise<void>;
  removeTimeMarker: (dataId: string, markerId: string) => Promise<void>;
  getTimeMarkers: (dataId: string) => TimeMarker[];

  // ============================================
  // NOVO: Funções de Campos Customizáveis (Fase 7)
  // ============================================
  loadActiveProjectCustomFields: () => Promise<ProjectField[]>;
  addCustomFieldToProject: (field: Omit<ProjectField, 'id' | 'createdAt' | 'order'>) => Promise<string>;
  updateCustomFieldInProject: (fieldId: string, updates: Partial<ProjectField>) => Promise<void>;
  removeCustomFieldFromProject: (fieldId: string) => Promise<void>;
  reorderCustomFieldsInProject: (fieldIds: string[]) => Promise<void>;
  addOptionToSelectField: (fieldId: string, option: string) => Promise<void>;
  removeOptionFromSelectField: (fieldId: string, option: string) => Promise<void>;
  getCachedCustomFields: () => ProjectField[];
  invalidateCustomFieldsCache: () => void;
  revalidateCustomFieldsIfStale: () => void;

  // Funções de valores de campos em dados
  setFieldValueInData: (dataId: string, fieldId: string, value: string | number | boolean | null) => Promise<void>;
  setFieldValuesBatch: (dataIds: string[], fieldId: string, value: string | number | boolean | null) => Promise<void>;
  getFieldValuesForData: (dataId: string) => FieldValue[];
}

// Zustand slice helper types
export type SliceSet = (partial: Partial<DriveProjectStore> | ((state: DriveProjectStore) => Partial<DriveProjectStore>)) => void;
export type SliceGet = () => DriveProjectStore;

// ============================================
// NOMENCLATURA DINÂMICA — Constantes e Helper
// ============================================

export const DEFAULT_PAGE_LABELS: Record<string, string> = {
  leituras: 'Leituras',
  horizonte: 'Horizonte',
  dadosPesquisa: 'Dados da Pesquisa',
  conexoes: 'Conexões',
  citacoes: 'Citações',
  colaboracao: 'Colaboração',
};

export const TEMPLATE_PAGE_LABELS: Record<string, Record<string, string>> = {
  pesquisa: { leituras: 'Leituras' },
  leitura: { leituras: 'Leituras' },
  personalizado: {},
};

export const TEMPLATE_SIDEBAR_DIVIDERS: Record<string, boolean | null> = {
  pesquisa: true,
  leitura: false,
  personalizado: null,
};

export function getPageLabel(
  pageId: string,
  project?: { pageLabels?: Record<string, string>; template?: string } | null
): string {
  if (project?.pageLabels?.[pageId]) {
    // Override: "Leituras e Fichamentos" → "Leituras" (legacy projects)
    if (pageId === 'leituras' && project.pageLabels[pageId] === 'Leituras e Fichamentos') {
      return 'Leituras';
    }
    return project.pageLabels[pageId];
  }
  if (project?.template) {
    const templateLabels = TEMPLATE_PAGE_LABELS[project.template];
    if (templateLabels?.[pageId]) return templateLabels[pageId];
  }
  return DEFAULT_PAGE_LABELS[pageId] || pageId;
}
